import type { DiarySondagePhoto } from '@ast-data/shared';
import {
    DIARY_FORM_NUMBER_FIELDS,
    type DiaryFormNumberField,
    type DiaryNumbers
} from './diaryNumbers';
import { filledSondagePhotos, sondagePhotoKey } from './sondagePhotoRows';

/**
 * What the previous Tagebuch day offers the day being written, and which of it has
 * already been taken.
 *
 * A "Letzte vergebene Nr." only changes on a day that hands one out, and a Sondage
 * stays open across days, so most days repeat most of what the day before them
 * said. Retyping it is busywork and a chance to mistype a number that is then
 * flagged as having gone backwards.
 *
 * Numbers (fields) and Sondagen (rows) are ONE list: from where the user stands
 * they are the same question, and the usual answer should be one click.
 *
 * Free of React and of the dialog, so it can be tested on its own. Labels are left
 * to the component.
 */

/**
 * Where an offer stands against the day being written:
 *
 * - `open`   — nothing in the way, the button fills it in.
 * - `taken`  — the day already says exactly this.
 * - `blocked`— the day says something ELSE: that was written deliberately, and a
 *   button that silently replaced it would be a trap. The row stays visible, it
 *   just carries no action.
 *
 * A Sondage is read the same way, by its PAIR with a Teilfoto: naming it with the
 * previous day's Teilfoto is `taken`, with a different one `blocked`, not at all
 * `open`.
 *
 * Per pair rather than per Sondage because a day may photograph one Sondage twice,
 * so the previous day can offer Sondage 2 with Teilfoto 3 AND 4. Taking the first
 * must not block the second, or the day holds half of what it was offered with no
 * way to ask for the rest. So while everything the day says about a Sondage came
 * from the previous day, its remaining entries stay `open`; the moment the day
 * names a Teilfoto of its own, they are `blocked` again.
 */
export type DiaryCarryOverState = 'open' | 'taken' | 'blocked';

/** One thing the previous day offers, and where it stands with this day. */
export type DiaryCarryOverOffer = {
    /** Stable React key, and what `onTake` names. */
    key: string;
    state: DiaryCarryOverState;
} & (
    | { kind: 'number'; field: DiaryFormNumberField; value: string }
    | { kind: 'sondagePhoto'; entry: DiarySondagePhoto }
);

/**
 * Everything the previous day has to offer, in the order the section lists it: the
 * typed numbers first, then its Sondage/Teilfoto entries. A number the previous
 * day left blank is no offer.
 */
export function diaryCarryOverOffers(
    previousNumbers: DiaryNumbers | null,
    previousSondagePhotos: readonly DiarySondagePhoto[],
    currentNumbers: Record<DiaryFormNumberField, string>,
    currentSondagePhotos: readonly DiarySondagePhoto[]
): DiaryCarryOverOffer[] {
    const offers: DiaryCarryOverOffer[] = [];
    for (const field of DIARY_FORM_NUMBER_FIELDS) {
        const value = (previousNumbers?.[field] ?? '').trim();
        if (value === '') {
            continue;
        }
        const current = (currentNumbers[field] ?? '').trim();
        offers.push({
            kind: 'number',
            key: field,
            field,
            value,
            state:
                current === ''
                    ? 'open'
                    : current === value
                      ? 'taken'
                      : 'blocked'
        });
    }
    // Grouped by what identifies an entry across days, so a Sondage photographed
    // twice is one group with two entries rather than two rows that overwrite
    // each other.
    const present = groupBySondage(currentSondagePhotos);
    const offered = groupBySondage(previousSondagePhotos);
    for (const [key, entries] of offered) {
        const today = present.get(key) ?? [];
        // Everything the day says about this Sondage came from the previous day,
        // so it is partway through carrying it over and the rest is still on
        // offer. A day that says nothing about it passes trivially.
        const onlyCarriedOver = today.every((row) =>
            entries.some((entry) => samePartialPhoto(entry, row))
        );
        for (const entry of entries) {
            offers.push({
                kind: 'sondagePhoto',
                /**
                 * The pair, not the Sondage: two entries of one Sondage are two
                 * offers and need two keys, or the list draws one over the other.
                 */
                key: `sondage:${key}:${photoPart(entry)}`,
                entry,
                state: today.some((row) => samePartialPhoto(row, entry))
                    ? 'taken'
                    : onlyCarriedOver
                      ? 'open'
                      : 'blocked'
            });
        }
    }
    return offers;
}

/** The Teilfoto as it is compared: trimmed and case-insensitive. */
function photoPart(entry: DiarySondagePhoto): string {
    return entry.partialPhoto.trim().toLowerCase();
}

/**
 * The entries of a day by their cross-day key, in the order they were written and
 * without a pair said twice — two offers of one pair would key alike.
 */
function groupBySondage(
    rows: readonly DiarySondagePhoto[]
): Map<string, DiarySondagePhoto[]> {
    const grouped = new Map<string, DiarySondagePhoto[]>();
    for (const row of filledSondagePhotos(rows)) {
        const key = sondagePhotoKey(row);
        const entries = grouped.get(key);
        if (!entries) {
            grouped.set(key, [row]);
        } else if (!entries.some((entry) => samePartialPhoto(entry, row))) {
            entries.push(row);
        }
    }
    return grouped;
}

/** Whether two entries of the same Sondage name the same Teilfoto. */
function samePartialPhoto(
    left: DiarySondagePhoto,
    right: DiarySondagePhoto
): boolean {
    return (
        left.partialPhoto.trim().toLowerCase() ===
        right.partialPhoto.trim().toLowerCase()
    );
}

/** The offers still to take — what "Alle N übernehmen" would apply. */
export function openCarryOverOffers(
    offers: readonly DiaryCarryOverOffer[]
): DiaryCarryOverOffer[] {
    return offers.filter((offer) => offer.state === 'open');
}
